import { Button, Tooltip } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';

import { conversationsApi } from 'apis/conversations';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppSelector } from 'utils/redux/useAppSelector';

/**
 * Allows to set conversation to resolved status, which prevents messaging.
 * Currently available only for email/custom_form channels.
 */
export const ConversationResolveButton = (): JSX.Element | null => {
  const notify = useNotifications();
  const hasReplyAccess = useHasAccess('conversation.reply');
  const flags = useFeatureFlag(['conversation-resolution-state']);
  const [updateConversationResolve, { isLoading }] =
    conversationsApi.useUpdateConversationResolveMutation();
  const { conversation, authorId } = useAppSelector((state) => ({
    conversation: state.conversation.metaConversation.conversation,
    authorId: state.settings.profile.profile.id,
  }));

  const { id: csid, initial_channel, resolution } = conversation;

  if (
    !flags.conversationResolutionState ||
    !csid ||
    (initial_channel !== 'email' && initial_channel !== 'contactform')
  ) {
    return null;
  }

  const toggleConversationResolution = async () => {
    try {
      await updateConversationResolve({
        csid,
        resolve: !resolution?.resolvedAt,
        author_id: authorId,
      }).unwrap();
      notify({
        title: 'Resolution status updated.',
        time: 3000,
        type: 'success',
        icon: 'done',
        status: 'success',
      });

      ampli.conversationResolutionStateToggled({
        resolutionStatus: resolution?.resolvedAt ? 'Unresolved' : 'Resolved',
      });
    } catch {
      notify(
        {
          title: 'Failed to update conversation resolution status.',
          icon: 'exclamation',
          className: 'danger',
          time: 5000,
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <Tooltip
      content={
        hasReplyAccess
          ? resolution?.resolvedAt
            ? 'Remove resolved conversation status'
            : 'Set conversation to resolved status'
          : "You don't have permission to change resolution status"
      }
    >
      <Button
        variant="secondary"
        isLoading={isLoading}
        disabled={!hasReplyAccess}
        onClick={toggleConversationResolution}
      >
        {resolution?.resolvedAt ? 'Unresolve' : 'Resolve'}
      </Button>
    </Tooltip>
  );
};
